import { EmblaCarouselType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { range } from "lodash-es";
import { CircleSmallIcon } from "lucide-react";
import React, { useEffect, useState } from "react";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";

function Carousel({ className, ...props }: React.ComponentProps<"div">) {
  const [emblaRef, emblaApi] = useEmblaCarousel({}, [Autoplay()]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.plugins().autoplay?.play();
  }, [emblaApi]);

  return (
    <div className="embla">
      <div className="embla__viewport overflow-hidden" ref={emblaRef}>
        <div
          className={cn(
            "embla__container",
            "flex touch-pan-y touch-pinch-zoom",
            className,
          )}
          {...props}
        ></div>
      </div>
      <div className="embla__controls">
        <div className="embla__dots text-center">
          {range(0, emblaApi?.slideNodes().length || 0).map((i) => (
            <CarouselDot key={i} index={i} emblaApi={emblaApi} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CarouselDot({
  emblaApi,
  index,
  ...props
}: {
  emblaApi?: EmblaCarouselType;
  index: number;
} & React.ComponentProps<"button">) {
  const [selected, setSelected] = useState(emblaApi?.selectedScrollSnap());

  useEffect(() => {
    if (emblaApi) {
      const cb = (api: EmblaCarouselType) =>
        setSelected(api.selectedScrollSnap());
      emblaApi?.on("select", cb);
      return () => {
        emblaApi?.off("select", cb);
      };
    }
  }, [emblaApi]);

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={(e) => {
        e.preventDefault();
        emblaApi?.plugins()?.autoplay?.reset();
        emblaApi?.scrollTo(index);
      }}
      {...props}
    >
      <CircleSmallIcon
        className={cn(
          index === selected
            ? "text-primary fill-primary"
            : "text-gray-500 fill-gray-500",
        )}
      />
    </Button>
  );
}

function CarouselSlide({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("grow-0 shrink-0 basis-full min-w-0", className)}
      {...props}
    />
  );
}

export { Carousel, CarouselSlide };
